import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { getExamSessions } from "../../../services/ExamSessionService";
import { getAttempts } from "../../../services/StudentAttemptService";
import { getOverviewStatistics, getAcademicYearStatistics } from "../../../services/StatisticsService";
import { useYearFilters, YearFilters } from "../Statistics/YearFilters";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../../utils/i18nLabels";
import { usePermissions } from "../../../utils/permissions";

const statCard = (title, value, icon, color) => ({ title, value, icon, color });

const Dashboard1 = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const userName = useSelector((state) => state.auth.auth?.user?.email) || "";
  // Ce tableau de bord est réservé à l'administration (voir DashboardRouter) :
  // l'enseignant et l'étudiant ont chacun le leur.
  const isStaff = true;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [counts, setCounts] = useState({ students: null, teachers: null, examsPublished: null, sessionsOngoing: null, averageOutOf20: null });
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [recentAttempts, setRecentAttempts] = useState([]);
  const [passRate, setPassRate] = useState(null);
  const [yearStats, setYearStats] = useState(null);
  const canReadYearStats = can("STATISTIC_READ");
  const filters = useYearFilters(canReadYearStats);
  const { yearId, classId } = filters;

  // Décisions de fin d'année (admis, crédits, redoublants, sortants, exclus)
  // selon les filtres. Chargé à part : un échec (pas d'année
  // courante, permission manquante) ne doit pas bloquer le reste du dashboard.
  useEffect(() => {
    if (!canReadYearStats || !yearId) return;
    getAcademicYearStatistics(yearId, filters.params)
      .then((statsRes) => setYearStats(statsRes?.data?.data || null))
      .catch(() => setYearStats(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadYearStats, filters.key]);

  // Cartes, sessions et tentatives : rechargées à chaque changement de filtre.
  useEffect(() => {
    if (!filters.ready) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const [ongoingRes, scheduledRes, attemptsRes] = await Promise.all([
          getExamSessions({ page: 1, limit: 5, status: "ongoing", ...(classId ? { class_id: classId } : {}) }),
          getExamSessions({ page: 1, limit: 5, status: "scheduled", ...(classId ? { class_id: classId } : {}) }),
          getAttempts({ page: 1, limit: 6, sort_by: "started_at", sort_dir: "desc" }),
        ]);
        if (cancelled) return;
        setUpcomingSessions([...(ongoingRes.data?.data || []), ...(scheduledRes.data?.data || [])].slice(0, 5));
        setRecentAttempts(attemptsRes.data?.data || []);

        try {
          const overviewRes = await getOverviewStatistics(filters.paramsWithYear);
          const overview = overviewRes.data?.data;
          if (cancelled) return;
          setCounts({
            students: overview.students,
            teachers: overview.teachers,
            examsPublished: overview.exams.published,
            sessionsOngoing: overview.sessions.ongoing,
            averageOutOf20: overview.results.averageOutOf20,
          });
          setPassRate(overview.results.passRate);
        } catch (statsErr) {
          // Compte sans STATISTIC_LIST : pas de chiffres agrégés.
          if (!cancelled) {
            setCounts({ students: null, teachers: null, examsPublished: null, sessionsOngoing: ongoingRes.data?.pagination?.total ?? 0, averageOutOf20: null });
            setPassRate(null);
          }
        }
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.message || t("common.ui.impossibleChargerStatistiques"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.ready, filters.key]);

  const statusLabels = { in_progress: t("common.examSessionStatus.ongoing"), submitted: t("common.attemptStatus.submitted"), timed_out: t("common.attemptStatus.timed_out"), cancelled: t("common.examSessionStatus.cancelled") };
  const statusBadge = { in_progress: "primary", submitted: "success", timed_out: "warning", cancelled: "danger" };

  const staffCards = useMemo(
    () => [
      statCard(t("menu.students.title"), counts.students, "ti ti-school", "primary"),
      statCard(t("menu.teachers.title"), counts.teachers, "ti ti-user-check", "info"),
      statCard(t("common.ui.examensPublies"), counts.examsPublished, "ti ti-clipboard-text", "success"),
      statCard(t("common.ui.sessionsCours"), counts.sessionsOngoing, "ti ti-broadcast", "warning"),
      statCard(t("pages.filters.averageOutOf20"), counts.averageOutOf20 !== null && counts.averageOutOf20 !== undefined ? `${counts.averageOutOf20} / 20` : null, "ti ti-target-arrow", "info"),
    ],
    [counts, t],
  );

  const yearCards = useMemo(
    () => (yearStats
      ? [
          statCard(t("pages.statistics.studentsAdmitted"), yearStats.admitted, "ti ti-circle-check", "success"),
          statCard(t("pages.statistics.studentsWithCredits"), yearStats.admittedWithCredits, "ti ti-progress-check", "info"),
          statCard(t("pages.statistics.studentsRepeating"), yearStats.repeating, "ti ti-repeat", "warning"),
          statCard(t("pages.statistics.studentsGraduated"), yearStats.graduated, "ti ti-certificate", "primary"),
          statCard(t("pages.statistics.studentsExcluded"), yearStats.excluded, "ti ti-user-x", "danger"),
          statCard(t("pages.statistics.studentsUndecided"), yearStats.undecided, "ti ti-hourglass", "secondary"),
        ]
      : []),
    [yearStats, t],
  );

  if (loading && counts.students === null && counts.sessionsOngoing === null && !filters.ready) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("common.crud.loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="bg-welcome-banner mb-3">
          <div className="card-body d-flex align-items-center justify-content-between">
            <h4 className="mb-0 text-white">{t("pages.dashboard.welcome", { name: userName })}</h4>
            {can("STATISTIC_LIST") && (
              <Link to="/statistics" className="btn btn-outline-light btn-sm text-white">
                <i className="ti ti-chart-bar me-1"></i>{t("pages.dashboard.detailedStatistics")}
              </Link>
            )}
          </div>
        </div>

        {loadError && <div className="alert alert-warning">{loadError}</div>}

        {isStaff && (
          <>
            <div className="card mb-3">
              <div className="card-body py-3">
                <YearFilters filters={filters} small />
              </div>
            </div>
            <div className="row">
              {staffCards.map((card) => (
                <div className="col-xl-4 col-sm-6 mb-3" key={card.title}>
                  <div className="card h-100">
                    <div className="card-body d-flex align-items-center justify-content-between">
                      <div>
                        <p className="mb-1 text-muted">{card.title}</p>
                        <h3 className="mb-0">{card.value ?? "-"}</h3>
                      </div>
                      <span className={`avatar avatar-lg bg-${card.color}-transparent rounded-circle d-flex align-items-center justify-content-center`}>
                        <i className={`${card.icon} text-${card.color} fs-24`}></i>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              <div className="col-xl-4 col-sm-6 mb-3">
                <div className="card h-100">
                  <div className="card-body d-flex align-items-center justify-content-between">
                    <div>
                      <p className="mb-1 text-muted">{t("common.ui.tauxReussite")}</p>
                      <h3 className="mb-0">{passRate !== null ? `${passRate}%` : "-"}</h3>
                      <small className="text-muted">{passRate !== null ? t("pages.dashboard.passRateAllResults") : t("pages.dashboard.passRateUnavailable")}</small>
                    </div>
                    <span className="avatar avatar-lg bg-success-transparent rounded-circle d-flex align-items-center justify-content-center">
                      <i className="ti ti-chart-pie text-success fs-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {canReadYearStats && yearId && (
              <div className="card mb-3">
                <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <h5 className="mb-0">{t("pages.dashboard.yearOutcomes", { year: yearStats?.academicYear?.name || "" })}</h5>
                </div>
                {yearStats && (
                  <div className="card-body pb-0">
                    <div className="row">
                      {yearCards.map((card) => (
                        <div className="col-xl-3 col-sm-6 mb-3" key={card.title}>
                          <div className="card h-100 border shadow-none mb-0">
                            <div className="card-body d-flex align-items-center justify-content-between">
                              <div>
                                <p className="mb-1 text-muted">{card.title}</p>
                                <h3 className="mb-0">{card.value ?? 0}</h3>
                              </div>
                              <span className={`avatar avatar-lg bg-${card.color}-transparent rounded-circle d-flex align-items-center justify-content-center`}>
                                <i className={`${card.icon} text-${card.color} fs-24`}></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="row">
              <div className="col-lg-6 mb-3">
                <div className="card h-100">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">{t("pages.dashboard.upcomingSessions")}</h5>
                    <Link to="/exam-sessions" className="btn btn-sm btn-outline-primary">
                      {t("pages.dashboard.viewAll")}
                    </Link>
                  </div>
                  <div className="card-body p-0">
                    {upcomingSessions.length > 0 ? (
                      <div className="list-group list-group-flush">
                        {upcomingSessions.map((session) => (
                          <div key={session.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                              <p className="mb-0 fw-medium">{t("pages.dashboard.session")} {new Date(session.start_at).toLocaleString(getLocale())}</p>
                              <small className="text-muted">{t("pages.dashboard.jusqu")} {new Date(session.end_at).toLocaleString(getLocale())}</small>
                            </div>
                            <span className={`badge badge-soft-${session.status === "ongoing" ? "primary" : "info"}`}>
                              {session.status === "ongoing" ? t("common.examSessionStatus.ongoing") : t("common.examSessionStatus.scheduled")}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted text-center py-4 mb-0">{t("pages.dashboard.noUpcomingSessions")}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6 mb-3">
                <div className="card h-100">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">{t("pages.dashboard.recentAttempts")}</h5>
                    <Link to="/attempts" className="btn btn-sm btn-outline-primary">
                      {t("pages.dashboard.viewAll")}
                    </Link>
                  </div>
                  <div className="card-body p-0">
                    {recentAttempts.length > 0 ? (
                      <div className="list-group list-group-flush">
                        {recentAttempts.map((attempt) => (
                          <div key={attempt.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                              <p className="mb-0 fw-medium">
                                {attempt.started_at ? new Date(attempt.started_at).toLocaleString(getLocale()) : "-"}
                              </p>
                            </div>
                            <span className={`badge badge-soft-${statusBadge[attempt.status] || "secondary"}`}>
                              {statusLabels[attempt.status] || attempt.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted text-center py-4 mb-0">{t("pages.dashboard.noRecentAttempts")}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard1;